import { Paper, Typography, TextField, Button, IconButton, Box } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import type { PRHighlightEpisode } from '../../../types/salesforce'
import { ImageUploadField } from './ImageUploadField'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

export interface HighlightEpisodeSectionProps {
  prSheetId?: string
  highlightEpisodes: PRHighlightEpisode[]
  onChange: (highlightEpisodes: PRHighlightEpisode[]) => void
  onBlur?: () => void
}

function SortableHighlightEpisodeItem({
  episode,
  index,
  onDelete,
  onChange,
  onBlur,
}: {
  episode: PRHighlightEpisode
  index: number
  onDelete: () => void
  onChange: (field: keyof PRHighlightEpisode, value: string) => void
  onBlur?: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: episode.Id! })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <Box ref={setNodeRef} style={style} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" {...attributes} {...listeners} sx={{ cursor: 'grab' }}>
            <DragIndicatorIcon />
          </IconButton>
          <Typography variant="subtitle2">エピソード #{index + 1}</Typography>
        </Box>
        <IconButton onClick={onDelete} size="small" color="error">
          <DeleteIcon />
        </IconButton>
      </Box>

      <TextField
        label="エピソードタイトル（28文字以内）"
        fullWidth
        margin="normal"
        value={episode.Title__c || ''}
        onChange={(e) => onChange('Title__c', e.target.value)}
        onBlur={onBlur}
        slotProps={{ htmlInput: { maxLength: 28 } }}
        helperText={`${(episode.Title__c || '').length}/28文字`}
      />

      <ImageUploadField
        label="エピソードイメージ"
        imageContentDocumentId={episode.ImageContentDocumentId__c}
        onImageUpload={(contentDocumentId) => onChange('ImageContentDocumentId__c', contentDocumentId)}
        onImageDelete={() => onChange('ImageContentDocumentId__c', '')}
        onBlur={onBlur}
        circular={false}
        linkedEntityId={episode.Id}
      />

      <TextField
        label="サマリー"
        fullWidth
        margin="normal"
        multiline
        rows={2}
        value={episode.Summary__c || ''}
        onChange={(e) => onChange('Summary__c', e.target.value)}
        onBlur={onBlur}
      />

      <TextField
        label="目的"
        fullWidth
        margin="normal"
        multiline
        rows={2}
        value={episode.Goal__c || ''}
        onChange={(e) => onChange('Goal__c', e.target.value)}
        onBlur={onBlur}
      />

      <TextField
        label="直面した課題"
        fullWidth
        margin="normal"
        multiline
        rows={2}
        value={episode.Challenges__c || ''}
        onChange={(e) => onChange('Challenges__c', e.target.value)}
        onBlur={onBlur}
      />

      <TextField
        label="メンバー構成"
        fullWidth
        margin="normal"
        multiline
        rows={2}
        value={episode.Team__c || ''}
        onChange={(e) => onChange('Team__c', e.target.value)}
        onBlur={onBlur}
      />

      <TextField
        label="役割と貢献"
        fullWidth
        margin="normal"
        multiline
        rows={2}
        value={episode.Role__c || ''}
        onChange={(e) => onChange('Role__c', e.target.value)}
        onBlur={onBlur}
      />

      <TextField
        label="具体的な取り組み"
        fullWidth
        margin="normal"
        multiline
        rows={3}
        value={episode.Actions__c || ''}
        onChange={(e) => onChange('Actions__c', e.target.value)}
        onBlur={onBlur}
      />

      <TextField
        label="成果"
        fullWidth
        margin="normal"
        multiline
        rows={2}
        value={episode.Results__c || ''}
        onChange={(e) => onChange('Results__c', e.target.value)}
        onBlur={onBlur}
      />
    </Box>
  )
}

export function HighlightEpisodeSection({
  prSheetId,
  highlightEpisodes,
  onChange,
  onBlur,
}: HighlightEpisodeSectionProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )
  const handleAdd = () => {
    const newEpisode: PRHighlightEpisode = {
      Id: `temp-${Date.now()}`,
      PRSheet__c: prSheetId,
      Title__c: '',
      ImageContentDocumentId__c: '',
      Summary__c: '',
      Goal__c: '',
      Challenges__c: '',
      Team__c: '',
      Role__c: '',
      Actions__c: '',
      Results__c: '',
      SortOrder__c: (highlightEpisodes.length + 1) * 10,
    }
    onChange([...highlightEpisodes, newEpisode])
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = highlightEpisodes.findIndex((ep) => ep.Id === active.id)
      const newIndex = highlightEpisodes.findIndex((ep) => ep.Id === over.id)

      const reorderedEpisodes = arrayMove(highlightEpisodes, oldIndex, newIndex)

      // SortOrder__cを更新
      const updatedEpisodes = reorderedEpisodes.map((ep, index) => ({
        ...ep,
        SortOrder__c: (index + 1) * 10,
      }))

      onChange(updatedEpisodes)
      if (onBlur) onBlur()
    }
  }

  const handleChange = (index: number, field: keyof PRHighlightEpisode, value: string) => {
    const newEpisodes = [...highlightEpisodes]
    newEpisodes[index] = {
      ...newEpisodes[index],
      [field]: value,
    }
    onChange(newEpisodes)
  }

  const handleDelete = (index: number) => {
    const newEpisodes = highlightEpisodes.filter((_, i) => i !== index)
    onChange(newEpisodes)
  }

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">⭐ 注力エピソード</Typography>
        <Button startIcon={<AddIcon />} onClick={handleAdd} variant="outlined" size="small">
          追加
        </Button>
      </Box>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={highlightEpisodes.map((ep) => ep.Id!)} strategy={verticalListSortingStrategy}>
          {highlightEpisodes.map((episode, index) => (
            <SortableHighlightEpisodeItem
              key={episode.Id}
              episode={episode}
              index={index}
              onDelete={() => handleDelete(index)}
              onChange={(field, value) => handleChange(index, field, value)}
              onBlur={onBlur}
            />
          ))}
        </SortableContext>
      </DndContext>

      {highlightEpisodes.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
          注力エピソードを追加してください
        </Typography>
      )}
    </Paper>
  )
}
